import isUndefined from 'lodash/isUndefined';
import type { IFareBreakup } from '../hooks/Flights/useGetItineraryFareBreakup';
import type { ServiceFee as ServiceFeeV1 } from '../types/api/v1/obt/pnr/payment';
import type { ServiceCharge } from '../types/api/v2/obt/model/service-charge';
import { MoneyUtil } from './Money';
import type { IAirRevalidateItineraryResponse, IServiceFeeData } from '../types';
import type { AirSelectedItineraryResponseManager } from '../services';
import type { ServiceFee } from '../types/api/v2/obt/model/service-fee';
import type { Money } from '../types/api/v1/common/money';

export interface ILocalisedCurrencyValue {
  currencyCode: string;
  amount: number;
  minFractionDigits?: number;
  maxFractionDigits?: number;
}

type ILocalisedCurrency = { value: ILocalisedCurrencyValue };
export interface IFareBreakupValue {
  label: string;
  id: string;
  dataTestId: string;
  dividerOnTop: boolean;
  dividerOnBottom: boolean;
  value: ILocalisedCurrency;
}

export function isAirExchangeRefundable(fareBreakup: IFareBreakup): boolean {
  const isRefund = fareBreakup.finalPrice.add(fareBreakup.changeFees).isNegative();
  return isRefund;
}

export function isAirExchangeFeePositive(fareBreakup: IFareBreakup): boolean {
  const isExchangeFeePositive = fareBreakup.finalPrice.add(fareBreakup.changeFees).isPositive();
  return isExchangeFeePositive;
}

export function isEvenExchange(fareBreakup: IFareBreakup): boolean {
  return fareBreakup.finalPrice.add(fareBreakup.changeFees).isZero();
}

function getTotalServiceFeesV1(serviceFees: MoneyUtil[]) {
  if (serviceFees.length < 1) {
    return MoneyUtil.zeroMoney();
  }

  const { otherCoinage } = serviceFees[0];
  const currency = serviceFees[0].getCurrency();
  const originalCurrency = serviceFees[0].getOriginalCurrency();

  let totalServiceFee = MoneyUtil.zeroMoneyWithOriginal(currency, originalCurrency, otherCoinage);

  serviceFees.forEach((fee) => {
    totalServiceFee = totalServiceFee.add(fee);
  });
  return totalServiceFee;
}

export function getTotalServiceFees(serviceCharges: ServiceCharge[]): Omit<IServiceFeeData, 'showTax'> {
  if (serviceCharges.length === 0) {
    return {
      total: MoneyUtil.zeroMoney(),
      tax: MoneyUtil.zeroMoney(),
      base: MoneyUtil.zeroMoney(),
    };
  }

  const parsedServiceChargeTaxes: MoneyUtil[] = [];
  const parsedServiceFeeBases: MoneyUtil[] = [];
  const parsedServiceFeeTotals: MoneyUtil[] = [];

  serviceCharges.forEach((serviceCharge) => {
    const allTaxes = serviceCharge.taxes.map((tax) => MoneyUtil.convertV2MoneyToMoneyUtil(tax.amount));
    const totalServiceChargeTax = getTotalServiceFeesV1(allTaxes);
    parsedServiceChargeTaxes.push(totalServiceChargeTax);

    parsedServiceFeeBases.push(MoneyUtil.convertV2MoneyToMoneyUtil(serviceCharge.amount));

    parsedServiceFeeTotals.push(MoneyUtil.convertV2MoneyToMoneyUtil(serviceCharge.totalAmount));
  });

  const total = getTotalServiceFeesV1(parsedServiceFeeTotals);
  const tax = getTotalServiceFeesV1(parsedServiceChargeTaxes);
  const base = getTotalServiceFeesV1(parsedServiceFeeBases);

  return { base, tax, total };
}

interface GetTripsTotalServiceFeeProps {
  serviceCharges: ServiceFeeV1[] | ServiceFee[];
  showServiceFeeTax: boolean;
}

export function getTripsTotalServiceFees({ serviceCharges, showServiceFeeTax }: GetTripsTotalServiceFeeProps) {
  if (serviceCharges.length === 0) {
    return {
      serviceFees: {
        base: MoneyUtil.zeroMoney(),
        tax: MoneyUtil.zeroMoney(),
        total: MoneyUtil.zeroMoney(),
        showTax: showServiceFeeTax ?? false,
      },
    };
  }

  if (
    serviceCharges.every((serviceCharge) => {
      return isUndefined(serviceCharge?.visibleToTraveler) ? false : !serviceCharge.visibleToTraveler;
    })
  ) {
    return {
      serviceFees: {
        base: MoneyUtil.parse(undefined),
        tax: MoneyUtil.parse(undefined),
        total: MoneyUtil.parse(undefined),
        showTax: showServiceFeeTax ?? false,
      },
    };
  }

  const parsedServiceChargeTaxes: MoneyUtil[] = [];
  const parsedServiceCharges: MoneyUtil[] = [];
  serviceCharges.forEach((serviceCharge) => {
    if (serviceCharge.fare?.tax) {
      parsedServiceChargeTaxes.push(MoneyUtil.parse(serviceCharge?.fare?.tax as unknown as Money));
    }
    /* old and new types have minor irrelvant difference that don't make a difference to this function's scope */
    parsedServiceCharges.push(MoneyUtil.parse(serviceCharge?.fare?.base as unknown as Money));
  });

  const base = getTotalServiceFeesV1(parsedServiceCharges);
  const tax = getTotalServiceFeesV1(parsedServiceChargeTaxes);
  const total = base.add(tax);

  return { serviceFees: { base, tax, total, showTax: showServiceFeeTax ?? false } };
}

export function getFareInfoForAirExchangeCheckout(
  airSelectedItineraryResponseManager: AirSelectedItineraryResponseManager,
  revalidateItineraryResponse: IAirRevalidateItineraryResponse | undefined,
): { isExchangeFeeZero: boolean; isExchangeFeePositive: boolean; isExchangeRefundable: boolean; totalFare: MoneyUtil } {
  let totalFare = MoneyUtil.zeroMoney();

  if (revalidateItineraryResponse) {
    totalFare = MoneyUtil.parse(revalidateItineraryResponse.totalFare);
  } else {
    totalFare = airSelectedItineraryResponseManager.GetTotalFare(0);
  }

  const isExchangeFeePositive = totalFare.isPositive();
  const isExchangeRefundable = totalFare.isNegative();

  const isExchangeFeeZero = totalFare.isZero();

  return { isExchangeFeeZero, isExchangeFeePositive, isExchangeRefundable, totalFare };
}
